import { useEffect, useRef } from 'react';
import { useGroupSecurityGating } from '@/hooks/useGroupSecurityGating';
import { useAuthStore } from '@/stores/auth-store';
import { useTeamStore } from '@/stores/team-store';

type AutoLandFolder = {
  name?: string;
  /** Parent path of the folder (root folders have '' / '/'); the target is parent + name. */
  path?: string | null;
  teamId?: string;
};

/**
 * Group Security v3 (§12, D8) — login home-folder auto-land marker.
 *
 * AuthProvider (which sits ABOVE the router and cannot navigate) records a one-time "pending home
 * land" on login via `markHomeLandPending()` after switching to the user's home team. The first
 * content page that mounts then consumes it here and navigates into `me.homeFolder`. Kept in
 * sessionStorage so it survives the AuthProvider→router→page mount hop but not a new login (cleared
 * on logout via `clearPersistedPaths` siblings / explicitly by `clearHomeLandPending`).
 */
const HOME_LAND_PENDING_KEY = 'scio3:homeLandPending';
// Per-browser-session guard (keyed by uid): the home-team auto-switch + land runs ONCE per session, NOT
// on every page load. TeamSwitcher does a full reload with no ?activeTeamId, so without this the login
// home-team switch would re-fire on that reload and bounce the user back to their home team — making it
// impossible to switch away. A genuinely new login (new tab / different uid) re-lands.
const HOME_LANDED_KEY = 'scio3:homeLandedFor';

/** Has the home-team auto-switch/land already run for this uid in this browser session? */
export function hasHomeLandedThisSession(uid: string): boolean {
  try {
    return sessionStorage.getItem(HOME_LANDED_KEY) === uid;
  } catch {
    return false;
  }
}

/** Mark the home-team auto-switch/land as done for this uid in this session. */
export function markHomeLandedThisSession(uid: string) {
  try {
    sessionStorage.setItem(HOME_LANDED_KEY, uid);
  } catch {
    // ignore
  }
}

/**
 * Called by AuthProvider on login with the user's home-folder TYPES (§12 per-type). Each content page
 * consumes its OWN type once, so EVERY page lands at its per-type home (Screens→device, Assets→asset,
 * …) — not just the first page that mounts. A single boolean marker (the old shape) was consumed by the
 * first page, so the other pages never landed.
 */
export function markHomeLandPending(types: string[]) {
  try {
    sessionStorage.setItem(HOME_LAND_PENDING_KEY, JSON.stringify([...new Set(types || [])]));
  } catch {
    // sessionStorage unavailable (private mode / SSR) — auto-land silently skipped.
  }
}

/** Clears the whole pending set (on logout / when there's nothing to land). */
export function clearHomeLandPending() {
  try {
    sessionStorage.removeItem(HOME_LAND_PENDING_KEY);
  } catch {
    // ignore
  }
}

function pendingTypes(): string[] {
  try {
    const raw = sessionStorage.getItem(HOME_LAND_PENDING_KEY);
    if (!raw) return [];
    // Back-compat: an old single-boolean marker ('1') → no per-type info, treat as none pending.
    if (raw === '1') return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter((t: unknown): t is string => typeof t === 'string') : [];
  } catch {
    return [];
  }
}

/** Is THIS content type still awaiting its one-time login land? */
function isTypePending(type: string): boolean {
  return pendingTypes().includes(type);
}

/** Consume ONE type from the pending set (leaves the others for their pages). */
function consumeTypePending(type: string) {
  try {
    const remaining = pendingTypes().filter((t) => t !== type);
    if (remaining.length) sessionStorage.setItem(HOME_LAND_PENDING_KEY, JSON.stringify(remaining));
    else sessionStorage.removeItem(HOME_LAND_PENDING_KEY);
  } catch {
    // ignore
  }
}

/**
 * Group Security — auto-land (spec §7.5 legacy + §12 D8 home folder).
 *
 * Called once by EACH of the five content surfaces, every one passing its own `homeType`:
 * Screens (`ScreensPage`, 'device') · Files (`AssetsPage`, 'asset') · Playlists (`PlaylistsPage`,
 * 'playlist') · Schedules (`ScheduleSidebar`, 'schedule') · Engage (`EngagePage`, 'engage').
 * A page that does not call this hook never lands its type — that was GX-13, where only Playlists
 * and Schedules were wired up, so Screens / Assets / Engage members stayed at the default folder.
 *
 * Two independent behaviors:
 * 1. Legacy scoped-member land (`useGroupSecurityGating().shouldAutoLand`) — NEUTRALIZED in v3 (the
 *    hook is a stable no-op), kept so existing call sites keep compiling.
 * 2. Home-folder land (v3 D8) — on login, if the user's group defines a home teamspace/folder,
 *    AuthProvider switches to the home team and marks EVERY home type pending; each content page
 *    then consumes its OWN type at ROOT and navigates into that type's `me.homeFolders` path.
 *    Consumed once per login, so a later manual Home-breadcrumb click back to root does NOT bounce
 *    the user forward again.
 *
 * `atRoot` must be FALSE in a page's virtual, non-folder locations (Shared with me, Favorites,
 * Engage's Recent view, Assets' DRIVE mode): the type is still consumed, but no navigation happens,
 * because lifting a user out of a virtual view is worse than not landing at all.
 *
 * No-ops for users without a configured home folder. Fires at most once per page mount (a guard ref).
 */
export function useAutoLandFolder(params: {
  atRoot: boolean;
  loading: boolean;
  folders: AutoLandFolder[];
  hasRootItems: boolean;
  onNavigate: (path: string, teamId?: string) => void;
  /** §12 per-type — THIS page's content type: 'device' (Screens) | 'asset' | 'playlist' | 'schedule' | 'engage'. */
  homeType: string;
}) {
  const { atRoot, loading, folders, hasRootItems, onNavigate, homeType } = params;
  const { shouldAutoLand } = useGroupSecurityGating();
  const homeTeamId = useAuthStore((s) => s.me?.homeTeamId);
  const homeFolders = useAuthStore((s) => s.me?.homeFolders);
  // §12 per-type: land at THIS page's OWN home folder (Screens→device, Assets→asset, …). No cross-type
  // fallback — a folder path from another content tree doesn't exist here, so landing there is wrong.
  const homeEntry = homeFolders?.find((h) => h.type === homeType);
  const homeFolder = homeEntry?.path;
  // Navigate into the home using the folder's OWN team (path is not unique across teams), falling back
  // to the login home team when the entry carries no teamId (legacy / dual-read).
  const homeFolderTeamId = homeEntry?.teamId ?? homeTeamId;
  const activeTeamId = useTeamStore((s) => s.activeTeamId);
  const landedRef = useRef(false);
  const homeLandedRef = useRef(false);

  // Behavior 1 — legacy scoped-member land (no-op in v3, guarded so it stays inert).
  useEffect(() => {
    if (landedRef.current || !atRoot || loading) return;
    if (!shouldAutoLand(folders.length, hasRootItems)) return;
    const only = folders[0];
    if (!only?.name) return;
    landedRef.current = true;
    const targetPath = only.path ? `${only.path}/${only.name}` : `/${only.name}`;
    onNavigate(targetPath, only.teamId);
  }, [atRoot, loading, folders, hasRootItems, shouldAutoLand, onNavigate]);

  // Behavior 2 — login home-folder land (§12 D8, per-type). Consumes THIS page's type from the pending
  // set once, so every content page lands at its own per-type home (not just the first page to mount).
  useEffect(() => {
    if (homeLandedRef.current || loading) return;
    if (!homeFolder || !isTypePending(homeType)) return;
    // The home folder path is team-scoped; only land once AuthProvider's team switch has settled.
    if (homeTeamId && activeTeamId !== homeTeamId) return;
    homeLandedRef.current = true;
    consumeTypePending(homeType);
    // Only navigate when the page is at its root — a deep-link elsewhere is an intentional
    // destination we must not override. Either way this type is consumed (once per login).
    if (atRoot) {
      onNavigate(homeFolder, homeFolderTeamId);
    }
  }, [
    atRoot,
    loading,
    homeFolder,
    homeTeamId,
    homeFolderTeamId,
    activeTeamId,
    onNavigate,
    homeType,
  ]);
}
